import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignDocumentModel, CampaignDocumentRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignDocuments']

type CampaignDocumentFilters = {
  campaign_id?: number,
}

type UseCampaignDocumentsOptions = {
  filters?: CampaignDocumentFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignDocumentRequestOptions,
}

function useCampaignDocuments(options: UseCampaignDocumentsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
  } = filters

  const {
    updatedEntities: { campaignDocuments: campaignDocumentsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignDocuments } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignDocuments = useMemo(() => {
    const filtered = Object.values(campaignDocuments).filter((campaignDocument: CampaignDocumentModel) => {
      const { campaign_id } = campaignDocument

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)

      return matchCampaignId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [campaignDocumentsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignDocumentsCount = filteredCampaignDocuments.length
  const hasFilteredCampaignDocuments = !!filteredCampaignDocumentsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignDocumentsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignDocuments } = useReduxAction(
    'campaignDocuments',
    'loadCampaignDocuments',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignDocuments,
    filteredCampaignDocumentsCount,
    hasFilteredCampaignDocuments,
    loading: loadingCampaignDocuments,
  }
}

export default useCampaignDocuments
